import { Formik } from "formik";
import { get } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdBusiness,
  MdCalendarToday,
  MdCheck,
  MdClose,
  MdDescription,
  MdEdit,
  MdMail,
  MdPerson,
  MdPhone,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  DATE_STRING_FORMAT,
  ORGANIZATION_TYPES,
  USER_TYPES,
} from "utils/constants";
import { displayDate } from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import validation from "../Detail/validation";

export default function EnterpriseInfo({ enterprise, onSubmit }) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const INFO = t("DETAIL.INFO_TAB", { returnObjects: true });
  const { t: errors } = useTranslation(NAME_SPACES.ERRORS);
  const VALIDATIONS = errors("VALIDATIONS", { returnObjects: true });

  const [isEditing, setIsEditing] = useState(false);

  // Find the HR user from the users array
  const hrUser = useMemo(() => {
    const user = enterprise.users?.find(
      (u) => u.meta?.type === USER_TYPES.HUMAN_RESOURCE.type,
    );
    return (
      user || {
        id: "",
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
      }
    );
  }, [enterprise.users]);

  const initialValues = useMemo(
    () => ({
      organization: {
        id: enterprise.id,
        name: enterprise.name || "",
        siretNumber: enterprise.siretNumber || "",
        counterpartyContracts: enterprise.counterpartyContracts || [],
        additionalInfo: {
          brokerCode: get(enterprise, "additionalInfo.brokerCode", ""),
          subGroupCode: get(enterprise, "additionalInfo.subGroupCode", ""),
          populationCode: get(enterprise, "additionalInfo.populationCode", ""),
          contributorType: get(
            enterprise,
            "additionalInfo.contributorType",
            "",
          ),
        },
      },
      user: {
        id: hrUser.id,
        firstname: hrUser.firstname || "",
        lastname: hrUser.lastname || "",
        email: hrUser.email || "",
        phone: hrUser.phone || "",
      },
    }),
    [enterprise, hrUser],
  );

  async function handleSubmit(values, { setSubmitting }) {
    try {
      await onSubmit({
        ...values,
        organization: {
          ...values.organization,
          additionalInfo: {
            ...get(enterprise, "additionalInfo", {}),
            brokerCode: get(
              values,
              "organization.additionalInfo.brokerCode",
              "",
            ),
            subGroupCode: get(
              values,
              "organization.additionalInfo.subGroupCode",
              "",
            ),
            populationCode: get(
              values,
              "organization.additionalInfo.populationCode",
              "",
            ),
            contributorType: get(
              values,
              "organization.additionalInfo.contributorType",
              "",
            ),
          },
        },
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating enterprise:", error);
      alert(INFO.ERROR_UPDATE);
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel(resetForm) {
    resetForm();
    setIsEditing(false);
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation(VALIDATIONS)}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}
    >
      {(formikProps) => {
        const {
          values,
          handleSubmit: formikHandleSubmit,
          isSubmitting,
          resetForm,
        } = formikProps;
        const formProps = { values, ...formikProps };

        return (
          <form onSubmit={formikHandleSubmit} className="company__space-y-6">
            <div className="company__flex company__justify-between company__items-center">
              <h2 className="company__text-xl company__font-semibold company__text-slate-900">
                {INFO.TITLE}
              </h2>
              {!isEditing ? (
                <Button type="primary" onClick={() => setIsEditing(true)}>
                  <MdEdit className="company__w-4 company__h-4" />
                  {INFO.EDIT}
                </Button>
              ) : (
                <div className="company__flex company__gap-2">
                  <Button
                    type="danger--outlined"
                    onClick={() => handleCancel(resetForm)}
                  >
                    <MdClose className="company__w-4 company__h-4" />
                    {INFO.CANCEL}
                  </Button>
                  <Button
                    type="success"
                    disabled={isSubmitting}
                    onClick={() => formikHandleSubmit()}
                  >
                    <MdCheck className="company__w-4 company__h-4" />
                    {isSubmitting ? INFO.SAVING : INFO.SAVE}
                  </Button>
                </div>
              )}
            </div>

            <div>
              <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
                {INFO.SECTIONS.ENTERPRISE}
              </h3>
              <div className="company__bg-slate-50 company__rounded-lg company__p-6 company__space-y-4">
                <div className="company__grid company__grid-cols-1 company__md-grid-cols-2 company__gap-6">
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdBusiness className="company__w-4 company__h-4" />
                      {INFO.FIELDS.NAME} *
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.name",
                          ...formProps,
                        })}
                        required
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {enterprise.name}
                      </p>
                    )}
                  </div>

                  {get(enterprise, "type") !== ORGANIZATION_TYPES.CDG && (
                    <div>
                      <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                        <MdDescription className="company__w-4 company__h-4" />
                        {INFO.FIELDS.SIRET} *
                      </label>
                      {isEditing ? (
                        <Input
                          maxLength={14}
                          {...bindInputProps({
                            name: "organization.siretNumber",
                            ...formProps,
                          })}
                          required
                          disabled={
                            get(enterprise, "type") === ORGANIZATION_TYPES.CDG
                          }
                        />
                      ) : (
                        <p className="company__text-slate-900 company__font-medium">
                          {enterprise.siretNumber || "N/A"}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdBusiness className="company__w-4 company__h-4" />
                      {INFO.FIELDS.ORGANIZATION_TYPE}
                    </label>
                    {isEditing ? (
                      <Input
                        value={
                          enterprise.type === ORGANIZATION_TYPES.CDG
                            ? INFO.FIELDS.ORGANIZATION_TYPE_COLLECTIVITY
                            : INFO.FIELDS.ORGANIZATION_TYPE_COMPANY
                        }
                        disabled
                      />
                    ) : (
                      <p className="company__text-slate-900">
                        {enterprise.type === ORGANIZATION_TYPES.CDG
                          ? INFO.FIELDS.ORGANIZATION_TYPE_COLLECTIVITY
                          : INFO.FIELDS.ORGANIZATION_TYPE_COMPANY}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdCalendarToday className="company__w-4 company__h-4" />
                      {INFO.FIELDS.CONTRACT_START_DATE}
                    </label>
                    {isEditing ? (
                      <Input
                        type="date"
                        format={DATE_FORMAT}
                        disabledDate={(current) => current < moment()}
                        {...bindDateInputProps({
                          name: "organization.counterpartyContracts.0.issuanceDate",
                          ...formProps,
                        })}
                        disabled
                      />
                    ) : (
                      <p className="company__text-slate-900">
                        {get(
                          values,
                          "organization.counterpartyContracts.0.issuanceDate",
                        )
                          ? displayDate({
                              date: get(
                                values,
                                "organization.counterpartyContracts.0.issuanceDate",
                              ),
                              format: DATE_STRING_FORMAT,
                            })
                          : "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdDescription className="company__w-4 company__h-4" />
                      {INFO.FIELDS.CONTRACT_NUMBER}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.counterpartyContracts.0.identifier",
                          ...formProps,
                        })}
                        disabled
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {get(
                          values,
                          "organization.counterpartyContracts.0.identifier",
                        ) || "-"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
                {INFO.SECTIONS.CIBLEMUT}
              </h3>
              <div className="company__bg-slate-50 company__rounded-lg company__p-6 company__space-y-4">
                <div className="company__grid company__grid-cols-1 company__md-grid-cols-2 company__gap-6">
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdDescription className="company__w-4 company__h-4" />
                      {INFO.FIELDS.BROKER_CODE}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.additionalInfo.brokerCode",
                          ...formProps,
                        })}
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {get(
                          values,
                          "organization.additionalInfo.brokerCode",
                        ) || "-"}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdDescription className="company__w-4 company__h-4" />
                      {INFO.FIELDS.SUB_GROUP_CODE}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.additionalInfo.subGroupCode",
                          ...formProps,
                        })}
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {get(
                          values,
                          "organization.additionalInfo.subGroupCode",
                        ) || "-"}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdDescription className="company__w-4 company__h-4" />
                      {INFO.FIELDS.POPULATION_CODE}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.additionalInfo.populationCode",
                          ...formProps,
                        })}
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {get(
                          values,
                          "organization.additionalInfo.populationCode",
                        ) || "-"}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdDescription className="company__w-4 company__h-4" />
                      {INFO.FIELDS.CONTRIBUTOR_TYPE}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "organization.additionalInfo.contributorType",
                          ...formProps,
                        })}
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {get(
                          values,
                          "organization.additionalInfo.contributorType",
                        ) || "-"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
                {INFO.SECTIONS.HR_CONTACT}
              </h3>
              <div className="company__bg-slate-50 company__rounded-lg company__p-6 company__space-y-4">
                <div className="company__grid company__grid-cols-1 company__md-grid-cols-2 company__gap-6">
                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdPerson className="company__w-4 company__h-4" />
                      {INFO.FIELDS.CONTACT_FIRSTNAME} *
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "user.firstname",
                          ...formProps,
                        })}
                        required
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {hrUser.firstname || "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdPerson className="company__w-4 company__h-4" />
                      {INFO.FIELDS.CONTACT_LASTNAME} *
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "user.lastname",
                          ...formProps,
                        })}
                        required
                      />
                    ) : (
                      <p className="company__text-slate-900 company__font-medium">
                        {hrUser.lastname || "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdMail className="company__w-4 company__h-4" />
                      {INFO.FIELDS.EMAIL} *
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "user.email",
                          ...formProps,
                        })}
                        required
                        disabled
                      />
                    ) : hrUser.email ? (
                      <a
                        href={`mailto:${hrUser.email}`}
                        className="company__text-blue-600 company__hover-text-blue-700 company__font-medium"
                      >
                        {hrUser.email}
                      </a>
                    ) : (
                      <p className="company__text-slate-500">-</p>
                    )}
                  </div>

                  <div>
                    <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                      <MdPhone className="company__w-4 company__h-4" />
                      {INFO.FIELDS.PHONE}
                    </label>
                    {isEditing ? (
                      <Input
                        {...bindInputProps({
                          name: "user.phone",
                          ...formProps,
                        })}
                      />
                    ) : hrUser.phone ? (
                      <a
                        href={`tel:${hrUser.phone}`}
                        className="company__text-blue-600 company__hover-text-blue-700 company__font-medium"
                      >
                        {hrUser.phone}
                      </a>
                    ) : (
                      <p className="company__text-slate-500">-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
                {INFO.SECTIONS.INTERNAL}
              </h3>
              <div className="company__bg-slate-50 company__rounded-lg company__p-6 company__space-y-4">
                <div>
                  <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                    <MdPerson className="company__w-4 company__h-4" />
                    {INFO.FIELDS.COMMERCIAL_REFERENT}
                  </label>
                  {isEditing ? (
                    <Input
                      {...bindInputProps({
                        name: "organization.counterpartyContracts.0.subjects.commercialReferent",
                        ...formProps,
                      })}
                    />
                  ) : (
                    <p className="company__text-slate-900">
                      {get(
                        values,
                        "organization.counterpartyContracts.0.subjects.commercialReferent",
                      ) || "-"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="company__flex company__items-center company__gap-2 company__text-sm company__font-medium company__text-slate-700 company__mb-2">
                    <MdDescription className="company__w-4 company__h-4" />
                    {INFO.FIELDS.INTERNAL_NOTES}
                  </label>
                  {isEditing ? (
                    <Input
                      type="textarea"
                      rows={3}
                      {...bindInputProps({
                        name: "organization.counterpartyContracts.0.subjects.internalNotes",
                        ...formProps,
                      })}
                    />
                  ) : (
                    <p className="company__text-slate-700 company__whitespace-pre-wrap">
                      {get(
                        values,
                        "organization.counterpartyContracts.0.subjects.internalNotes",
                      ) || "-"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
                {INFO.SECTIONS.STATUS}
              </h3>
              <div className="company__bg-slate-50 company__rounded-lg company__p-6">
                <div className="company__flex company__items-center company__gap-3">
                  <span
                    className={`company__inline-flex company__items-center company__px-4 company__py-2 company__rounded-full company__text-sm company__font-medium ${
                      enterprise.active
                        ? "company__bg-green-100 company__text-green-800"
                        : "company__bg-slate-200 company__text-slate-700"
                    }`}
                  >
                    {enterprise.active
                      ? INFO.STATUS.ACTIVE
                      : INFO.STATUS.INACTIVE}
                  </span>
                  {get(
                    values,
                    "organization.counterpartyContracts.0.createdDate",
                  ) && (
                    <span className="company__text-sm company__text-slate-600">
                      {INFO.STATUS.CREATED_ON}{" "}
                      {displayDate({
                        date: get(
                          values,
                          "organization.counterpartyContracts.0.createdDate",
                        ),
                        format: DATE_STRING_FORMAT,
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
}
